import { useState, useEffect } from 'react';
import { Save, Plus, Trash2, Edit, Loader2, BookOpen } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';
import { ImagePicker } from './shared';

import { CDN_URL } from '@/config/cdn';

const LineageEditor = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // null = closed, { isNew: true } = new, { id, ... } = editing
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    quote: '',
    order: 0,
    is_published: true
  });

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/lineage`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSections(data);
      }
    } catch (error) {
      setMessage('❌ Failed to load lineage sections');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image_url: '',
      quote: '',
      order: sections.length,
      is_published: true
    });
  };

  const handleCreate = () => {
    resetForm();
    setEditingSection({ isNew: true });
  };

  const handleEdit = (section) => {
    setFormData({
      title: section.title,
      content: section.content,
      image_url: section.image_url || '',
      quote: section.quote || '',
      order: section.order,
      is_published: section.is_published !== false
    });
    setEditingSection(section);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      setMessage('❌ Please provide title and content');
      return;
    }

    setSaving(true);
    try {
      const url = editingSection.isNew
        ? `${process.env.REACT_APP_BACKEND_URL}/api/lineage`
        : `${process.env.REACT_APP_BACKEND_URL}/api/lineage/${editingSection.id}`;

      const method = editingSection.isNew ? 'POST' : 'PUT';

      // Clean data - if image_url is empty, set to null for logo fallback
      const cleanData = {
        ...formData,
        image_url: formData.image_url || null,
        quote: formData.quote || null
      };

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (response.ok) {
        setMessage('✅ Section saved successfully!');
        setEditingSection(null);
        fetchSections();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to save'}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/lineage/${sectionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('✅ Section deleted successfully!');
        fetchSections();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const error = await response.json();
        setMessage(`❌ ${error.detail || 'Failed to delete'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Lineage & History"
        description="Manage lineage sections, founder info, and martial arts history"
        icon={<BookOpen className="h-6 w-6" />}
        actions={
          <Button onClick={handleCreate} variant="adminPrimary">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </Button>
        }
      />

      {message && (
        <div className={`p-4 rounded ${
          message.includes('✅') ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' :
          'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Sections List */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          Sections ({sections.length})
        </h3>

        {sections.length === 0 ? (
          <Card className="border-dashed">
            <div className="text-center py-12">
              <BookOpen className="h-12 w-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
              <p className="text-gray-600 dark:text-gray-300 mb-2">No sections yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add your first lineage section to get started</p>
              <Button onClick={handleCreate} variant="adminPrimary">
                <Plus className="h-4 w-4 mr-2" />
                Add Section
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <Card key={section.id} className="p-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={section.image_url || `${CDN_URL}/images/owaza-logo.jpg`}
                      alt={section.title}
                      className="h-24 w-24 object-cover rounded"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-lg mb-1">
                      {section.title}
                      {section.is_published === false && (
                        <span className="ml-2 align-middle text-xs font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          Hidden
                        </span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">{section.content}</p>
                    {section.quote && (
                      <p className="text-xs italic text-gray-500 dark:text-gray-400 mb-2">"{section.quote}"</p>
                    )}
                    <div className="flex flex-wrap gap-2 items-center">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(section)}>
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(section.id)}>
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                      <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">Order: {section.order}</span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={!!editingSection} onOpenChange={(open) => !open && setEditingSection(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection?.isNew ? 'Add Section' : 'Edit Section'}
            </DialogTitle>
            <DialogDescription>
              {editingSection?.isNew
                ? 'Create a new lineage section'
                : 'Update section details'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Our Founder"
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="min-h-[150px]"
                placeholder="Main content for this section..."
              />
            </div>

            <div>
              <Label>Image (Optional - Logo will show if no image)</Label>
              <div className="space-y-2 mt-2">
                <ImagePicker
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  context="lineage"
                  previewSize="md"
                />
                {!formData.image_url && (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 bg-blue-50 dark:bg-blue-900/30 p-3 rounded border border-blue-200 dark:border-blue-800">
                    <div className="flex-shrink-0">
                      <img src={`${CDN_URL}/images/owaza-logo.jpg`} alt="Owaza Logo" className="h-12 w-12 rounded" />
                    </div>
                    <p>No image uploaded. Owaza logo will be displayed on this section.</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Quote (Optional)</Label>
              <Textarea
                value={formData.quote}
                onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                rows={2}
                placeholder="Optional inspiring quote..."
              />
            </div>

            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Lower numbers appear first</p>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label>Show on public Lineage page</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingSection(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} variant="adminPrimary" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingSection?.isNew ? 'Add Section' : 'Save Changes'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LineageEditor;
